import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaPlay, FaIdCard, FaCalendarAlt, FaFileAlt,
  FaBell, FaCog, FaHeadset, FaDollarSign,
  FaChevronRight, FaList, FaStar,
  FaClock, FaRoute
} from 'react-icons/fa';
import ServiceTracker from '../../DELIVERY/Service/ServiceTracker';
import DriverIncomingRequests from '../DriverIncomingRequests';
import DriverCommissionInfo from '../DriverCommissionInfo';
import PendingReviewsSection from '../PendingReviewsSection';
import LiveMapSection from '../LiveMapSection';
import GPSDistanceTracker from '../../GPS/GPSDistanceTracker';
import PanicButton from '../../UI/PanicButton';
import BrySwitch from '../../UI/BryUI/BrySwitch';
import BottomSheet from '../../UI/BottomSheet';
import TaskCard from '../../DELIVERY/Service/TaskCard';
import StatCard from '../../UI/StatCard';
import GaugeCircle from '../../UI/GaugeCircle';
import ProgressBar from '../../UI/ProgressBar';
import NotificationBanner from '../../UI/NotificationBanner';
import EmptyState from '../../UI/EmptyState';
import DriverStatsGrid from './components/DriverStatsGrid';
import DriverWalletCard from './components/DriverWalletCard';
import DriverActionRow from './components/DriverActionRow';
import DriverToolsMenu from './components/DriverToolsMenu';
import DriverServicePreferencesCard from './components/DriverServicePreferencesCard';
import { expireStaleSearchingServices, getDriverServices } from '../../DELIVERY/services/deliveryServices';
import { checkPendingDocuments, getDriverDocumentStatusDetails } from '../../../services/roleViewsServices';
import { useAuth } from '../../USER/Auth/AuthContext';
import { DRIVER_STATUSES, SERVICE_STATUSES } from '../../../constants';
import './DriverView.css';

const DriverView = ({
  driverStatus, gpsOnline, setGpsOnline, gpsError, clearGpsError,
  selectedPeriod, setSelectedPeriod, totalEarnings, totalKm, totalHoursWorked, earningPerKm, averageConsumption, bikeStats,
  pickupPos, dropoffPos, handleMapClick, selectedDriver, setSelectedDriver, clearPositions,
  setShowEarningModal, setShowFuelModal, setShowCalcModal, setShowMaintModal,
  walletBalance = 0, minWalletBalance = 0, walletTransactions = []
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [docCheckError, setDocCheckError] = useState(null);
  const [sheetExpanded, setSheetExpanded] = useState(true);
  const [driverTasks, setDriverTasks] = useState([]);

  useEffect(() => {
    expireStaleSearchingServices();
    const interval = setInterval(() => expireStaleSearchingServices(), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function loadDriverTasks() {
      if (!currentUser?.id) return;
      try {
        const services = await getDriverServices(currentUser.id);
        const activeOrRecent = (services || []).filter(s => 
          [SERVICE_STATUSES.DRIVER_ASSIGNED, SERVICE_STATUSES.IN_PROGRESS, SERVICE_STATUSES.AWAITING_CONFIRMATION].includes(s.status)
        );
        setDriverTasks(activeOrRecent);
      } catch (err) {
        console.error('Error fetching driver tasks:', err);
      }
    }
    loadDriverTasks();
    const handleServiceUpdatedEvent = () => {
      loadDriverTasks();
    };
    window.addEventListener('bryemma-service-updated', handleServiceUpdatedEvent);
    return () => {
      window.removeEventListener('bryemma-service-updated', handleServiceUpdatedEvent);
    };
  }, [currentUser?.id]);

  const handleGpsToggle = async () => {
    if (gpsOnline) {
      setGpsOnline(false);
      setDocCheckError(null);
      return;
    }

    // Regla del Modelo de Negocio: Saldo prepagado obligatorio para laborar
    if (parseFloat(minWalletBalance || 0) > 0 && parseFloat(walletBalance || 0) < parseFloat(minWalletBalance || 0)) {
      setDocCheckError(`Tu saldo en billetera ($${parseFloat(walletBalance || 0).toFixed(2)}) es insuficiente para laborar. Debes tener un saldo prepagado positivo para recibir servicios y cubrir la comisión de plataforma. Por favor contacta a administración para recargar.`);
      return;
    }

    try {
      if (currentUser?.id) {
        const docStatus = await getDriverDocumentStatusDetails(currentUser.id);
        if (docStatus.blocked) {
          setDocCheckError(docStatus.reason || "No puedes activar el GPS mientras tengas documentos pendientes o rechazados. Por favor actualiza tus documentos.");
          return;
        }
      }
      setDocCheckError(null);
      setGpsOnline(true);
    } catch (err) {
      console.error("Error al verificar documentos:", err);
      setDocCheckError("Ocurrió un error al verificar tus documentos. Inténtalo de nuevo.");
    }
  };

  if (driverStatus && driverStatus !== DRIVER_STATUSES.ACTIVE) {
    return (
      <div className="dv-pending-card">
        <span className="pending-icon">⏳</span>
        <h4>Registro en espera de activación</h4>
        <p>
          Tu cuenta de conductor está registrada y pendiente de aprobación por el equipo administrativo.
          <br />
          <strong>Para confirmar tu activación, debes dirigirte a nuestras oficinas físicas a consignar y validar tus documentos.</strong>
        </p>
        <div className="dv-pending-actions">
          <Link to="/documentos" className="bry-btn bry-btn-primary">
            <FaFileAlt className="me-2" /> Subir o Ver Documentos
          </Link>
          <Link to="/soporte" className="bry-btn bry-btn-ghost">
            <FaHeadset className="me-2" /> Contactar Soporte
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-figma-container">
      {/* Primary Section: Live Map (Top 60% in Figma) */}
      <div className="dv-map-wrapper">
        <LiveMapSection
          pickupPos={pickupPos}
          dropoffPos={dropoffPos}
          handleMapClick={handleMapClick}
          selectedDriver={selectedDriver}
          setSelectedDriver={setSelectedDriver}
          clearPositions={clearPositions}
        />
        <GPSDistanceTracker />
      </div>

      {/* Figma Bottom Sheet (Tasks, Requests & Routes) */}
      <BottomSheet
        title="Jornada de Hoy"
        date={new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: '2-digit' })}
        variant="persistent"
        isExpanded={sheetExpanded}
        onToggle={setSheetExpanded}
        className="dv-tasks-bottom-sheet"
      >
        {/* GPS Switch prominently placed at the very top of BottomSheet */}
        <div className="dv-gps-toggle-row mb-3">
          <div className="dv-gps-info">
            <div className={`dv-gps-dot ${gpsOnline ? 'online' : 'offline'}`} />
            <div>
              <div className="dv-gps-label">{gpsOnline ? 'GPS ACTIVADO' : 'GPS DESACTIVADO'}</div>
              <div className="dv-gps-sublabel">{gpsOnline ? 'Disponible para servicios' : 'No compartes ubicación'}</div>
            </div>
          </div>
          <BrySwitch checked={gpsOnline} onChange={handleGpsToggle} />
        </div>

        {/* In-Sheet GPS and Document Alerts */}
        {gpsError && (
          <div className="alert alert-warning py-2 px-3 mb-3 d-flex justify-content-between align-items-center rounded-12" style={{ fontSize: '0.8rem' }} onClick={clearGpsError}>
            <span>⚠️ {gpsError}</span>
            <button type="button" className="btn btn-sm p-0 ms-2 text-dark fw-bold border-0 bg-transparent" onClick={clearGpsError}>✕</button>
          </div>
        )}

        {docCheckError && (
          <div className="alert alert-danger py-2 px-3 mb-3 d-flex flex-column gap-2 rounded-12" style={{ fontSize: '0.8rem' }}>
            <span>🚫 {docCheckError}</span>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-dark" onClick={() => navigate('/documentos')}>
                Ir a Mis Documentos
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setDocCheckError(null)}>
                Cerrar
              </button>
            </div>
          </div>
        )}

        {/* Active Service Tracker & Incoming Requests */}
        <ServiceTracker />
        <DriverCommissionInfo />
        <DriverIncomingRequests />
        <DriverServicePreferencesCard />
        <PendingReviewsSection />

        {/* Active Tasks List (Figma DailyTasks_01_RoutOpen.png) / Empty State (DailyTasks_02_NoTask.png) */}
        {driverTasks.length > 0 ? (
          <div className="dv-tasks-section mb-3">
            <div className="section-header mb-2" style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#71717a' }}>
              Ruta de Entregas ({driverTasks.length})
            </div>
            {driverTasks.map((task, idx) => (
              <TaskCard
                key={task.service_id || idx}
                number={idx + 1}
                name={task.dropoff?.contact_name || task.pickup?.contact_name || (task.type === 'delivery' ? 'Entrega de Paquete' : 'Viaje de Pasajero')}
                address={task.dropoff?.address || task.pickup?.address || 'Ubicación seleccionada'}
                tracking={task.tracking_token ? `TRK-${task.tracking_token.slice(0, 8).toUpperCase()}` : null}
                time={task.created_at ? new Date(task.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '10:30'}
                active={task.status === SERVICE_STATUSES.IN_PROGRESS}
                onClick={() => navigate('/historial-servicios')}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Sin rutas activas"
            subtitle="Tus entregas y viajes asignados aparecerán organizados en esta lista."
          />
        )}

        {/* Stats Grid 2x2 */}
        <DriverStatsGrid 
            totalEarnings={totalEarnings} 
            totalKm={totalKm} 
            totalHoursWorked={totalHoursWorked} 
        />

        {/* Billetera Card */}
        <DriverWalletCard 
            walletBalance={walletBalance} 
            minWalletBalance={minWalletBalance} 
        />

        {/* Action Row */}
        <DriverActionRow />

        {/* Tools Menu */}
        <DriverToolsMenu />
      </BottomSheet>
    </div>
  );
};

export default DriverView;
